import { useState, useRef, useCallback } from 'react'
import {
  IoDocumentTextOutline,
  IoCloseOutline,
  IoCloudUploadOutline,
  IoDownloadOutline,
  IoTrashOutline,
  IoEyeOutline,
  IoImageOutline,
  IoGridOutline,
  IoCodeSlashOutline,
  IoChevronBackOutline,
} from 'react-icons/io5'
import { motion, AnimatePresence } from 'framer-motion'
import {
  useFilesByPatient,
  useCreateFile,
  useDeleteFile,
  useDownloadFile,
} from '../hooks/useFiles'
import { LoadingSpinner } from './ui/LoadingSpinner'
import toast from 'react-hot-toast'
import type { Patient } from '../types'

interface PatientDocumentsModalProps {
  patient: Patient
  isOpen: boolean
  onClose: () => void
}

const formatDate = (date: string) =>
  new Date(date).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' })
const formatSize = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`
}

function FileIcon({ mimeType, name }: { mimeType?: string | null; name: string }) {
  const ext = name.split('.').pop()?.toLowerCase()
  const isImage = mimeType?.startsWith('image/') ?? ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext ?? '')
  const isSheet = mimeType?.includes('sheet') || ['xls', 'xlsx', 'csv'].includes(ext ?? '')
  const isCode = ['json', 'xml', 'txt'].includes(ext ?? '')
  if (isImage) return <IoImageOutline className='text-emerald-400 text-2xl' />
  if (isSheet) return <IoGridOutline className='text-green-400 text-2xl' />
  if (isCode) return <IoCodeSlashOutline className='text-blue-400 text-2xl' />
  return <IoDocumentTextOutline className='text-blue-400 text-2xl' />
}

function PdfPreview({ dataUrl, name, onClose }: { dataUrl: string; name: string; onClose: () => void }) {
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className='fixed inset-0 bg-black/80 backdrop-blur-sm z-[60] flex flex-col'>
      <div className='flex items-center justify-between px-4 py-3 bg-gray-900 border-b border-gray-700'>
        <button onClick={onClose} className='flex items-center gap-2 text-gray-300 hover:text-white transition-colors text-sm'>
          <IoChevronBackOutline /> Retour
        </button>
        <span className='text-white font-medium text-sm truncate max-w-xs'>{name}</span>
        <a href={dataUrl} download={name}
          className='flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-500 hover:bg-blue-600 text-white text-xs font-medium transition-colors'>
          <IoDownloadOutline /> Télécharger
        </a>
      </div>
      <div className='flex-1 bg-gray-800'>
        <iframe src={dataUrl} title={name} className='w-full h-full border-0' />
      </div>
    </motion.div>
  )
}

function ImagePreview({ dataUrl, name, onClose }: { dataUrl: string; name: string; onClose: () => void }) {
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className='fixed inset-0 bg-black/90 backdrop-blur-sm z-[60] flex flex-col items-center justify-center'
      onClick={onClose}>
      <div className='absolute top-4 right-4 flex gap-2'>
        <a href={dataUrl} download={name} onClick={e => e.stopPropagation()}
          className='p-2 rounded-xl bg-white/10 hover:bg-white/20 text-white transition-colors'>
          <IoDownloadOutline />
        </a>
        <button onClick={onClose} className='p-2 rounded-xl bg-white/10 hover:bg-white/20 text-white transition-colors'>
          <IoCloseOutline />
        </button>
      </div>
      <img src={dataUrl} alt={name} className='max-w-full max-h-[85vh] object-contain rounded-lg shadow-2xl' onClick={e => e.stopPropagation()} />
      <p className='text-white/60 text-sm mt-3'>{name}</p>
    </motion.div>
  )
}

export default function PatientDocumentsModal({ patient, isOpen, onClose }: PatientDocumentsModalProps) {
  const [isUploading, setIsUploading] = useState(false)
  const [isDragging, setIsDragging] = useState(false)
  const [previewFile, setPreviewFile] = useState<{ dataUrl: string; name: string; type: 'pdf' | 'image' } | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const { data: filesData, isLoading } = useFilesByPatient(patient.id)
  const createFile = useCreateFile()
  const deleteFile = useDeleteFile()
  const downloadFile = useDownloadFile()

  const files = filesData?.data ?? []

  const processFile = useCallback(async (file: File) => {
    if (file.size > 50 * 1024 * 1024) { toast.error('Le fichier dépasse 50 MB'); return }
    setIsUploading(true)
    try {
      const reader = new FileReader()
      const dataUrl = await new Promise<string>((resolve, reject) => {
        reader.onload = () => resolve(reader.result as string)
        reader.onerror = reject
        reader.readAsDataURL(file)
      })
      const base64 = dataUrl.split(',')[1] ?? dataUrl
      await createFile.mutateAsync({
        name: file.name,
        type: file.name.split('.').pop()?.toUpperCase() ?? 'FILE',
        mimeType: file.type,
        data: base64,
        size: file.size,
        patientId: patient.id,
        description: `Document pour ${patient.firstName} ${patient.lastName}`,
      })
    } catch {
      toast.error("Erreur lors de l'upload")
    } finally {
      setIsUploading(false)
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }, [createFile, patient])

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0]; if (f) void processFile(f)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault(); setIsDragging(false)
    const f = e.dataTransfer.files[0]; if (f) void processFile(f)
  }

  const handleDownload = async (fileId: number, fileName: string) => {
    try {
      const blob = await downloadFile.mutateAsync(fileId)
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a'); a.href = url; a.download = fileName; a.click()
      URL.revokeObjectURL(url)
    } catch { toast.error('Erreur de téléchargement') }
  }

  const handlePreview = async (fileId: number, fileName: string, mimeType?: string | null) => {
    try {
      const blob = await downloadFile.mutateAsync(fileId)
      const mime = mimeType ?? blob.type
      const dataUrl = URL.createObjectURL(blob)
      if (mime === 'application/pdf') {
        setPreviewFile({ dataUrl, name: fileName, type: 'pdf' })
      } else if (mime.startsWith('image/')) {
        setPreviewFile({ dataUrl, name: fileName, type: 'image' })
      } else {
        const a = document.createElement('a'); a.href = dataUrl; a.download = fileName; a.click()
        URL.revokeObjectURL(dataUrl)
        toast('Fichier téléchargé (aperçu non disponible pour ce type)')
      }
    } catch { toast.error("Erreur lors du chargement de l'aperçu") }
  }

  const handleDelete = async (fileId: number, fileName: string) => {
    if (!window.confirm(`Supprimer "${fileName}" ?`)) return
    await deleteFile.mutateAsync(fileId)
  }

  const canPreview = (mimeType?: string | null, name?: string) => {
    const ext = (name ?? '').split('.').pop()?.toLowerCase()
    return mimeType === 'application/pdf' || mimeType?.startsWith('image/') ||
      ['pdf', 'jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext ?? '')
  }

  if (!isOpen) return null

  return (
    <>
      <AnimatePresence>
        {previewFile && previewFile.type === 'pdf' && (
          <PdfPreview dataUrl={previewFile.dataUrl} name={previewFile.name} onClose={() => setPreviewFile(null)} />
        )}
        {previewFile && previewFile.type === 'image' && (
          <ImagePreview dataUrl={previewFile.dataUrl} name={previewFile.name} onClose={() => setPreviewFile(null)} />
        )}
      </AnimatePresence>

      <AnimatePresence>
        <div className='fixed inset-0 z-50 flex items-center justify-center p-4'>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={onClose} className='absolute inset-0 bg-black/70 backdrop-blur-sm' />

          <motion.div initial={{ opacity: 0, scale: 0.9, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.9 }}
            className='relative bg-white dark:bg-dark-50 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col'>

            {/* Header */}
            <div className='flex items-center justify-between p-5 border-b border-gray-200 dark:border-gray-800'>
              <div className='flex items-center gap-3'>
                <div className='bg-blue-500/10 p-2 rounded-lg'>
                  <IoDocumentTextOutline className='text-blue-400 text-xl' />
                </div>
                <div>
                  <h2 className='text-lg font-bold text-gray-900 dark:text-gray-100'>
                    Documents — {patient.firstName} {patient.lastName.toUpperCase()}
                  </h2>
                  <p className='text-sm text-gray-500 dark:text-gray-400'>
                    {files.length} document{files.length !== 1 ? 's' : ''}
                  </p>
                </div>
              </div>
              <button onClick={onClose} className='p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors'>
                <IoCloseOutline className='text-xl text-gray-500' />
              </button>
            </div>

            {/* Content */}
            <div className='flex-1 overflow-y-auto p-5'>
              {/* Upload zone drag & drop */}
              <div
                onDragOver={e => { e.preventDefault(); setIsDragging(true) }}
                onDragLeave={() => setIsDragging(false)}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
                className={`mb-5 rounded-2xl border-2 border-dashed p-8 text-center cursor-pointer transition-all ${
                  isDragging
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-500/10'
                    : 'border-gray-300 dark:border-gray-700 hover:border-blue-500/50 bg-gray-50 dark:bg-dark-100'
                }`}>
                <input ref={fileInputRef} type='file' className='hidden' onChange={handleFileChange} disabled={isUploading}
                  accept='application/pdf,image/*,.doc,.docx,.xls,.xlsx,.txt' />
                {isUploading ? (
                  <div className='flex flex-col items-center gap-3'>
                    <LoadingSpinner />
                    <p className='text-gray-500 dark:text-gray-400 text-sm'>Upload en cours…</p>
                  </div>
                ) : (
                  <div className='flex flex-col items-center gap-2'>
                    <IoCloudUploadOutline className={`text-4xl transition-colors ${isDragging ? 'text-blue-500' : 'text-gray-400'}`} />
                    <p className='font-medium text-gray-700 dark:text-gray-300 text-sm'>
                      {isDragging ? 'Déposez le fichier ici' : 'Glissez-déposez ou cliquez pour uploader'}
                    </p>
                    <p className='text-xs text-gray-400'>PDF, images, documents — max 50 MB</p>
                  </div>
                )}
              </div>

              {/* Liste */}
              {isLoading ? (
                <div className='flex justify-center py-12'><LoadingSpinner /></div>
              ) : files.length === 0 ? (
                <div className='text-center py-12'>
                  <IoDocumentTextOutline className='text-gray-300 dark:text-gray-600 text-5xl mx-auto mb-3' />
                  <p className='text-gray-500 dark:text-gray-400'>Aucun document pour ce patient</p>
                </div>
              ) : (
                <div className='space-y-2'>
                  {files.map((file, index) => {
                    const isPDF = file.mimeType === 'application/pdf' || file.name.endsWith('.pdf')
                    const isImg = file.mimeType?.startsWith('image/') ?? false
                    const preview = canPreview(file.mimeType, file.name)
                    return (
                      <motion.div key={file.id}
                        initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.04 }}
                        className='flex items-center gap-4 p-4 rounded-xl bg-gray-50 dark:bg-dark-100 border border-gray-200 dark:border-gray-700 hover:border-blue-500/40 transition-colors'>
                        <div className='bg-blue-500/10 p-2.5 rounded-lg flex-shrink-0'>
                          <FileIcon mimeType={file.mimeType} name={file.name} />
                        </div>
                        <div className='flex-1 min-w-0'>
                          <p className='font-semibold text-gray-900 dark:text-gray-100 text-sm truncate'>{file.name}</p>
                          <div className='flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400 mt-0.5 flex-wrap'>
                            <span className='font-medium uppercase'>{file.type ?? 'FILE'}</span>
                            <span>·</span>
                            <span>{formatSize(file.size)}</span>
                            <span>·</span>
                            <span>{formatDate(file.createdAt)}</span>
                            {isPDF && <span className='text-blue-500 font-medium'>PDF</span>}
                            {isImg && <span className='text-emerald-500 font-medium'>Image</span>}
                          </div>
                        </div>
                        <div className='flex items-center gap-1 flex-shrink-0'>
                          {preview && (
                            <button
                              onClick={() => void handlePreview(file.id, file.name, file.mimeType)}
                              disabled={downloadFile.isPending}
                              className='p-2 rounded-lg bg-emerald-500/10 hover:bg-emerald-500/20 border border-emerald-500/30 text-emerald-500 transition-colors'
                              title='Aperçu'>
                              <IoEyeOutline className='text-base' />
                            </button>
                          )}
                          <button
                            onClick={() => void handleDownload(file.id, file.name)}
                            disabled={downloadFile.isPending}
                            className='p-2 rounded-lg bg-blue-500/10 hover:bg-blue-500/20 border border-blue-500/30 text-blue-400 transition-colors'
                            title='Télécharger'>
                            <IoDownloadOutline className='text-base' />
                          </button>
                          <button
                            onClick={() => void handleDelete(file.id, file.name)}
                            disabled={deleteFile.isPending}
                            className='p-2 rounded-lg bg-red-500/10 hover:bg-red-500/20 border border-red-500/30 text-red-400 transition-colors'
                            title='Supprimer'>
                            <IoTrashOutline className='text-base' />
                          </button>
                        </div>
                      </motion.div>
                    )
                  })}
                </div>
              )}
            </div>
          </motion.div>
        </div>
      </AnimatePresence>
    </>
  )
}
